import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useOnboardingStore } from "@/stores/onboardingStore";
import { useAuth } from "@/hooks/useAuth";
import { useSeatPersonalization } from "@/hooks/useSeatPersonalization";
import { OnboardingLayout } from "@/components/onboarding/OnboardingLayout";
import { StepTransition } from "@/components/onboarding/StepTransition";
import { StepWorkspace } from "@/components/onboarding/StepWorkspace";
import { StepTeamInvite } from "@/components/onboarding/StepTeamInvite";
import { StepBranding } from "@/components/onboarding/StepBranding";
import { StepPersonalization } from "@/components/onboarding/StepPersonalization";
import { createBatchInvites } from "@/lib/api/batchInvites";
import { updateWorkspaceSettings } from "@/lib/api/workspaces";
import type { PendingInvite, BrandingData } from "@/stores/onboardingStore";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const Onboarding = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const {
    currentStep,
    workspaceId,
    pendingInvites,
    brandingData,
    setStep,
    setWorkspace,
    setPendingInvites,
    setBrandingData,
    reset,
  } = useOnboardingStore();

  const { personalization, loading: personalizationLoading, savePersonalization } = useSeatPersonalization(
    workspaceId,
    user?.id || null
  );
  
  const [isInitializing, setIsInitializing] = useState(true);
  const [pendingStep, setPendingStep] = useState<number | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [transitionDirection, setTransitionDirection] = useState<"forward" | "backward">("forward");
  const [previousStep, setPreviousStep] = useState(currentStep);
  const [isProcessing, setIsProcessing] = useState(false);

  // Track transition direction
  useEffect(() => {
    if (currentStep !== previousStep) {
      setTransitionDirection(currentStep > previousStep ? "forward" : "backward");
      setPreviousStep(currentStep);
    }
  }, [currentStep, previousStep]);

  useEffect(() => {
    const initializeOnboarding = async () => {
      // CRITICAL: Wait until auth is loaded
      if (authLoading) return;
      
      setIsInitializing(true);
      
      const {
        data: { user },
      } = await supabase.auth.getUser();
      
      if (!user) {
        navigate("/");
        return;
      }

      const { data: memberships } = await supabase
        .from("workspace_members")
        .select("workspace_id, personalization_completed_at, workspaces(*)")
        .eq("user_id", user.id)
        .eq("seat_status", "active");

      if (memberships && memberships.length > 0) {
        // If personalization is completed, reset store and redirect to dashboard
        if (memberships[0].personalization_completed_at) {
          reset();
          navigate('/dashboard');
          return;
        }
        
        // Otherwise set workspace and go to personalization (step 4)
        setWorkspace(memberships[0].workspace_id);
        setStep(4);
      } else {
        // No workspace membership, start at step 1
        setStep(1);
      }
      
      setIsInitializing(false);
    };

    initializeOnboarding();
  }, [navigate, setWorkspace, setStep, reset, authLoading]);

  const handleWorkspaceComplete = (id: string) => {
    setWorkspace(id);
    setStep(2); // Go to team invite
  };

  const handleTeamInviteComplete = async (invites: PendingInvite[]) => {
    if (!workspaceId || !user) return;
    
    setIsProcessing(true);
    try {
      const result = await createBatchInvites({
        workspaceId,
        invites,
        createdBy: user.id,
      });

      if (result.successful > 0) {
        toast.success(`${result.successful} Einladung${result.successful === 1 ? '' : 'en'} erfolgreich versendet`);
      }
      
      if (result.failed > 0) {
        toast.error(`${result.failed} Einladung${result.failed === 1 ? '' : 'en'} fehlgeschlagen`);
        result.errors.forEach(err => {
          console.error(`Failed to invite ${err.email}:`, err.error);
        });
      }

      setPendingInvites(invites);
      setStep(3); // Go to branding
    } catch (error) {
      console.error('Error sending invites:', error);
      toast.error("Fehler beim Versenden der Einladungen");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleTeamInviteSkip = () => {
    setStep(3); // Skip to branding
  };

  const handleBrandingComplete = async (data: BrandingData) => {
    if (!workspaceId) return;
    
    setIsProcessing(true);
    try {
      await updateWorkspaceSettings({
        workspaceId,
        primaryColor: data.primaryColor,
        industry: data.industry,
        description: data.description,
      });
      
      setBrandingData(data);
      toast.success("Workspace-Einstellungen gespeichert");
      setStep(4); // Go to personalization
    } catch (error) {
      console.error('Error updating workspace settings:', error);
      toast.error("Fehler beim Speichern der Einstellungen");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBrandingSkip = () => {
    setStep(4); // Skip to personalization
  };

  const handlePersonalizationComplete = async (data: {
    job_role: string;
    job_title?: string;
    preferred_decision_areas: string[];
    team_size?: string;
    company_phase?: string;
    decision_style?: string[];
    communication_preference?: string;
    detail_level?: string;
    current_priorities?: string[];
  }) => {
    try {
      await savePersonalization(data);
      reset(); // Reset onboarding store to clean up localStorage
      navigate('/dashboard');
    } catch (error) {
      console.error('Error saving personalization:', error);
      toast.error("Fehler beim Speichern der Personalisierung");
    }
  };

  const renderStep = () => {
    let stepContent;
    
    switch (currentStep) {
      case 1:
        stepContent = <StepWorkspace onComplete={handleWorkspaceComplete} />;
        break;
      case 2:
        if (!workspaceId) {
          stepContent = null;
        } else {
          stepContent = (
            <StepTeamInvite 
              workspaceId={workspaceId}
              maxSeats={5}
              onComplete={handleTeamInviteComplete}
              onSkip={handleTeamInviteSkip}
            />
          );
        }
        break;
      case 3:
        stepContent = (
          <StepBranding 
            onComplete={handleBrandingComplete}
            onSkip={handleBrandingSkip}
          />
        );
        break;
      case 4:
        stepContent = <StepPersonalization onComplete={handlePersonalizationComplete} />;
        break;
      default:
        stepContent = null;
    }

    return (
      <StepTransition stepKey={currentStep} direction={transitionDirection}>
        {stepContent}
      </StepTransition>
    );
  };

  const stepNames = ["Workspace", "Team einladen", "Branding", "Personalisierung"];

  const hasUnsavedChanges = () => {
    switch (currentStep) {
      case 2:
      case 3:
      case 4:
        return true; // Team invite, branding, personalization - always confirm
      default:
        return false;
    }
  };

  const handleStepNavigation = (step: number) => {
    if (hasUnsavedChanges()) {
      setPendingStep(step);
      setShowConfirmDialog(true);
    } else {
      setStep(step);
    }
  };

  const handleConfirmNavigation = () => {
    if (pendingStep !== null) {
      setStep(pendingStep);
      setPendingStep(null);
    }
    setShowConfirmDialog(false);
  };

  const handleCancelNavigation = () => {
    setPendingStep(null);
    setShowConfirmDialog(false);
  };

  if (isInitializing || isProcessing) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          {isProcessing && (
            <p className="text-sm text-muted-foreground">
              Verarbeite Daten...
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <OnboardingLayout 
        currentStep={currentStep} 
        totalSteps={4} 
        stepNames={stepNames}
        onStepClick={handleStepNavigation}
      >
        {renderStep()}
      </OnboardingLayout>

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Zurück zum vorherigen Schritt?</AlertDialogTitle>
            <AlertDialogDescription>
              Wenn Sie zurück gehen, gehen möglicherweise nicht gespeicherte Änderungen im aktuellen Schritt verloren.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelNavigation}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmNavigation}>
              Trotzdem zurück
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default Onboarding;
